import {ApiClient} from "./apiClient";
import {ApiResponse} from "./types";
import {appConfig} from "../config";
import {Employee, AnnualLeaveBalance, WorkTimeBalance} from "./EmployeeApi";
import {LeaveRequest, LeaveRequestCreateRequest} from "./RequestApi";
import {UserAccount} from "./UserAccountApi";
import {UserSettings} from "./SettingsApi";
import {ClockInRequest, ClockOutRequest, TimeRecord} from "./TimetrackingApi";
import {ApprovalInstance, CancelRequest} from "./ApprovalApi";
import {Workday} from "./WorkDayRecordApi";


const meApiClient = new ApiClient(`${appConfig.backendUrl}/api/me`);

/**
 * Fetch current user's account information
 * @returns UserAccount with employee details, roles, permissions, and account status
 */
export const fetchMyUserAccount = (): Promise<ApiResponse<UserAccount>> => {
    return meApiClient.get<UserAccount>('/user-account');
};

/**
 * Fetch current user's employee data
 * @returns Complete Employee object for the logged-in user
 */
export const fetchMyEmployee = (): Promise<ApiResponse<Employee>> => {
    return meApiClient.get<Employee>('/employee');
};

/**
 * Fetch current user's annual leave balance
 * @returns Annual leave balance with vacation days data
 */
export const fetchMyLeaveBalance = (): Promise<ApiResponse<AnnualLeaveBalance>> => {
    return meApiClient.get<AnnualLeaveBalance>('/leave-balance');
};

export const fetchMyWorkTimeBalance = (): Promise<ApiResponse<WorkTimeBalance[]>> => {
    return meApiClient.get<WorkTimeBalance[]>('/work-time-balances');
};

/**
 * Fetch all leave requests created by the current user
 * @returns Array of leave requests
 */
export const fetchMyLeaveRequests = (): Promise<ApiResponse<LeaveRequest[]>> => {
    return meApiClient.get<LeaveRequest[]>('/requests/leaves');
};

/**
 * Submits a new leave request
 * @returns The created leave request
 */
export const createMyLeaveRequests = (
    requestData: LeaveRequestCreateRequest
): Promise<ApiResponse<LeaveRequest>> => {
    return meApiClient.post<LeaveRequest>('/requests/leaves', requestData);
};

/**
 * Fetch current user's settings/preferences
 * @returns User settings object
 */
export const fetchMySettings = (): Promise<ApiResponse<UserSettings>> => {
    return meApiClient.get<UserSettings>('/settings');
};

/**
 * Fetch partial user settings by name
 * @param name - Settings section name
 * @returns Partial user settings
 */
export const fetchMyPartialSettings = (name: string): Promise<ApiResponse<Partial<UserSettings>>> => {
    return meApiClient.get<Partial<UserSettings>>(`/settings/${name}`);
};

/**
 * Update current user's settings (partial update)
 * @param settings - Partial settings to update
 * @returns Updated settings
 */
export const updateMySettings = (settings: Partial<UserSettings>): Promise<ApiResponse<Partial<UserSettings>>> => {
    return meApiClient.post<Partial<UserSettings>>('/settings', settings);
};

/**
 * Replace current user's settings (full replacement)
 * @param settings - Complete settings object
 * @returns Updated settings
 */
export const replaceMySettings = (settings: UserSettings): Promise<ApiResponse<UserSettings>> => {
    return meApiClient.put<UserSettings>('/settings', settings);
};

// ── Time Tracking API ────────────────────────────────────────────────────────



/**
 * Fetch all approval instances pending action by the current user
 * @returns Array of pending approval instances
 */
export const fetchMyPendingApprovals = (): Promise<ApiResponse<ApprovalInstance[]>> => {
    return meApiClient.get<ApprovalInstance[]>('/approvals/pending');
};

/**
 * Fetch all approval instances pending action by the current user
 * @returns Array of pending approval instances
 */
export const cancelMyApproval = <T extends ApprovalInstance>(
    instanceId: string,
    request: CancelRequest
): Promise<ApiResponse<T>> => {
    return meApiClient.delete<T>(`/approvals/${instanceId}/cancel`, request);
};


export const fetchTimeRecordsRange = (from: string, to: string): Promise<ApiResponse<TimeRecord[]>> => {
    return meApiClient.get<TimeRecord[]>(`/time-records?from=${from}&to=${to}`);
};


/**
 * Work day records
 */

/**
 * Fetch today's time tracking summary including the active entry and all
 * completed entries for the current calendar day.
 */
export const fetchMyTodayTimeTracking = (): Promise<ApiResponse<Workday>> => {
    return meApiClient.get<Workday>('/work-time/today');
};

export const fetchWorkDayRecordsRange = (from: string, to: string): Promise<ApiResponse<Workday[]>> => {
    return meApiClient.get<Workday[]>(`/work-time/workdays?from=${from}&to=${to}`);
};

/**
 * Clock in the current user. Fails if the user is already clocked in.
 * @returns The newly created TimeEntry
 */
export const clockIn = (request: ClockInRequest = {}): Promise<ApiResponse<Workday>> => {
    return meApiClient.post<Workday>('/work-time/start-work-session', request);
};

/**
 * Clock out the current user. Fails if the user is not clocked in.
 * @returns The closed TimeEntry with durationMinutes populated
 */
export const clockOut = (request: ClockOutRequest = {}): Promise<ApiResponse<Workday>> => {
    return meApiClient.post<Workday>('/work-time/stop-work-session', request);
};